import { useQuery } from '@tanstack/react-query';
import { useApiMutation } from '../../api/mutations';
import { nexusBearerFetch } from '../../nexus/nexusClient';
import { useAuth } from '../../auth/AuthProvider';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { nexusJobsFixture, nexusJobDetailFixture } from './fixture';

/**
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-jobs-monitor.md
 * Endpoints: /v1/jobs (kein /admin-Prefix) — Operator-Bearer-Auth
 *
 * Wir injizieren den Bearer manuell weil der Bearer-Proxy in server.js
 * den Authorization-Header durchreicht (kein automatisches Bearer-Setting
 * vom Browser-Default-fetch).
 */
function authHeader(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** GET /v1/jobs?limit=100&status=&type=&subscription_id= */
export function useNexusJobs({ status = '', type = '', subscriptionId = '', limit = 100 } = {}) {
  const auth = useAuth();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-jobs', { status, type, subscriptionId, limit }, fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return nexusJobsFixture({ status, type, subscriptionId });
      }
      const qs = new URLSearchParams();
      qs.set('limit', String(limit));
      if (status) qs.set('status', status);
      // type-Param kennt /v1/jobs nicht (Audit B4) — wir filtern clientside
      // damit der UI-Filter trotzdem funktioniert, ohne Backend-Change.
      if (subscriptionId) qs.set('subscription_id', subscriptionId);
      const res = await nexusBearerFetch(`/v1/jobs?${qs.toString()}`, {
        headers: authHeader(auth.getAccessToken()),
      });
      let list = res?.jobs ?? res?.items ?? (Array.isArray(res) ? res : []);
      if (type) {
        const needle = type.toLowerCase();
        list = list.filter((j) => (j.type ?? '').toLowerCase().includes(needle));
      }
      return { jobs: list, raw: res };
    },
    refetchInterval: 15_000,
    staleTime: 5_000,
  });
}

/** GET /v1/jobs/:id */
export function useNexusJobDetail(jobId) {
  const auth = useAuth();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-job', jobId, fixturesMode ? 'fixture' : 'live'],
    enabled: !!jobId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return nexusJobDetailFixture(jobId);
      }
      const res = await nexusBearerFetch(`/v1/jobs/${encodeURIComponent(jobId)}`, {
        headers: authHeader(auth.getAccessToken()),
      });
      return res?.job ?? res;
    },
  });
}

/** POST /v1/jobs/:id/cancel */
export function useCancelNexusJob() {
  const auth = useAuth();
  return useApiMutation({
    live: (_fetch, { id }) =>
      nexusBearerFetch(`/v1/jobs/${encodeURIComponent(id)}/cancel`, {
        method: 'POST',
        headers: authHeader(auth.getAccessToken()),
      }),
    fixture: ({ id }) => ({ ok: true, id, status: 'cancelled' }),
    invalidate: ['nexus-jobs', 'nexus-job'],
    successMessage: (_, { id }) => `Job ${id} abgebrochen.`,
  });
}

/** POST /v1/jobs/:id/retry */
export function useRetryNexusJob() {
  const auth = useAuth();
  return useApiMutation({
    live: (_fetch, { id }) =>
      nexusBearerFetch(`/v1/jobs/${encodeURIComponent(id)}/retry`, {
        method: 'POST',
        headers: authHeader(auth.getAccessToken()),
      }),
    fixture: ({ id }) => ({ ok: true, new_job_id: `${id}-retry-${Date.now()}` }),
    invalidate: ['nexus-jobs'],
    successMessage: (res) =>
      res?.new_job_id ? `Retry-Job ${res.new_job_id} enqueued.` : 'Retry ausgelöst.',
  });
}
